import { Injectable } from '@angular/core';
import { deepCopy } from '@delon/util';
import { DialogService } from './dialog.service';
import { _HttpClient } from '@delon/theme';
import { URLS } from '../constant/interface';
import { _MSGTYPES } from '../constant/msgType';
import { TemplateEditData } from '../msg-interface/data-model';

type Mode = 'edit' | 'add';

@Injectable({ providedIn: 'root' })
export class TemplateService {
  /** 数据源 */
  source: { [key: number]: TemplateEditData } = {};
  /** 页面模式 */
  mode: Mode = 'edit';

  /** 消息类型索引 */
  MSG_TYPE = _MSGTYPES.MT;
  // 模板复用关系表
  provinceRefMap = {
    '0': [this.MSG_TYPE.SMS, this.MSG_TYPE.VOICE, this.MSG_TYPE.JPUSH],
    '2': [this.MSG_TYPE.WECHAT],
    '3': [this.MSG_TYPE.SMS, this.MSG_TYPE.VOICE, this.MSG_TYPE.JPUSH],
    '4': [this.MSG_TYPE.SMS, this.MSG_TYPE.VOICE, this.MSG_TYPE.EMAIL, this.MSG_TYPE.JPUSH],
    '6': [this.MSG_TYPE.SMS, this.MSG_TYPE.VOICE, this.MSG_TYPE.JPUSH],
    '8': [this.MSG_TYPE.WORKWX, this.MSG_TYPE.SMS, this.MSG_TYPE.VOICE, this.MSG_TYPE.JPUSH],
    '9': [this.MSG_TYPE._5G],
  };

  /** 标题 */
  get title() {
    return this.mode === 'edit' ? '更新融合通信模板' : '新增融合通信模板';
  }

  constructor(private dialogService: DialogService, private http: _HttpClient) {}

  /** 设置页面模式 */
  setMode(value: Mode): void {
    this.mode = value;
  }

  /** 设置数据源 */
  setSource(data: any): any {
    this.source = data;
    return this.source;
  }
  /** 清空数据源 */
  clear() {
    this.setSource([]);
  }
  /**
   * 添加模板
   * @param source 模板对象
   * @param type 消息类型
   */
  addTemplate(source: any, type: number): void {
    const target = this.getTpl(type);

    if (target) {
      // 先判断后增加
      const { maxTemplateLimit } = this.source[type];
      if (target.length + 1 > maxTemplateLimit) {
        this.dialogService.notification.warning(this.title, `一个消息类型最多创建${maxTemplateLimit}个模版`);
        return;
      }
      target.push(source);
      this.updatePriority(type);
    } else {
      // 新增消息类型
      this.source[type] = {
        templates: [...source],
      };
    }
  }

  /**
   * 删除模板
   * @param index 模板队列的索引
   * @param type 消息类型
   */
  removeTemplate(index: number, type: number): void {
    const { ignoreAudit, useTmplMethod } = this.source[type];
    const target = this.getTpl(type);

    // if (ignoreAudit === false && Number(useTmplMethod) === 2) {
    //   const total = this.getTpllen();
    //   if (total === 1) {
    //     this.dialogService.notification.error(this.title, `非发送免审，模板形式为【普通模板】时，当该业务类型仅剩一个模板时，模板不可删除；`);
    //     return;
    //   }
    // }
    if (ignoreAudit === true && Number(useTmplMethod) === 2 && target.length === 1) {
      this.dialogService.notification.error(this.title, `发送免审，模板形式为【普通模板】时，当该消息类型仅剩一个模板时，模板不可删除；`);
      return;
    }

    const { id, name } = target[index];
    const param = {
      id,
      name,
      state: -1,
    };
    this.reqForChangeTemplate(URLS.templateDelete.url, param, () => {
      this.dialogService.notification.success(this.title, `删除成功！`);
      target.splice(index, 1);
      // target.forEach((it: any, i: number) => {
      //   if (it.copyValue === htmlId) {
      //     it.copyValue = null;
      //   }
      //   it.priority = i + 1;
      // });
      this.updatePriority(type);
    });
  }

  /**
   * 移动模板
   * @param current 当前模板在模板队列中的索引
   * @param next 希望移动的位置索引
   * @param type 消息类型
   */
  moveTemplate(current: number, next: number, type: number): void {
    const target = this.getTpl(type);

    let upTemp = target[current];
    let downTemp = target[next];
    target[current] = downTemp;
    target[next] = upTemp;

    this.updatePriority(type);
  }

  /**
   * 复用模板
   * @param current 当前模板在模板队列中的索引
   * @param next 希望拷贝的模板的位置索引
   * @param type 消息类型
   */
  copyTemplate(current: number, next: number, type: number): void {
    const target = this.getTpl(type);
    const copyTarget = deepCopy(target[next]);
    const { name, isDefault } = target[current];
    target[current] = {
      ...copyTarget,
      name,
      isDefault,
      copyValue: next,
    };

    this.updatePriority(type);
  }
  /**
   * 校验名称的唯一性
   * @param name 新的名称
   * @param index 当前模板在模板队列中的索引
   * @param type 消息类型
   */
  validateName(name: string, index: number, type: number): void {
    const target = this.getTpl(type);
    target.forEach(item => {
      item.valid = target.filter(v => v.name === name).length < 2 ? true : false;
    });
  }

  /**
   * 改变模板状态
   * @param name 新的名称
   * @param index 当前模板在模板队列中的索引
   * @param type 消息类型
   */
  changeTemplateState(isOpen: boolean, index: number, type: number): void {
    const target = this.getTpl(type);
    const state = isOpen ? 1 : 0;
    const tip = isOpen ? '启用' : '停用';
    const { id, name } = target[index];
    const param = {
      id,
      name,
      state,
    };
    this.reqForChangeTemplate(URLS.templateUpdateState.url, param, () => {
      this.dialogService.notification.success(this.title, `${tip}成功！`);
      target[index].state = state;
    });
  }

  /** 模板状态变动请求 */
  private reqForChangeTemplate(url: string, paylod: any, cb?: CallableFunction): void {
    this.http.post(url, paylod).subscribe(
      data => {
        if (data.status === 0) {
          cb();
          return;
        }
        this.dialogService.notification.error(this.title, data.errorMsg);
      },
      () => {
        this.dialogService.notification.error(this.title, '操作失败，系统错误！');
      },
    );
  }

  /** 获取所有模板 */
  getAllTpl(): any {
    const temp = [];
    Object.keys(this.source).forEach(key => temp.push(this.source[key]));
    return temp;
  }

  /** 获取对应消息类型的模板 */
  getTpl(type: number): any {
    if (!this.source) return false;
    return (this.source[type] && this.source[type].templates) || false;
  }
  /** 获取对应消息类型下的最后一个模板 */
  getLastTpl(type: number): any {
    const result = this.getTpl(type);
    if (result) {
      return result[result.length - 1];
    }
  }

  /** 获取对应的消息类型 */
  getMsg(type: number): any {
    return this.source[type] || [];
  }
  /** 获取对应消息类型下的标准模板 */
  getStandardTpl(type: number): any {
    return this.source[type].standardTemplate || {};
  }

  /**
   * 获取模板数量
   * @param type 可选， 具体的消息类型
   */
  getTpllen(type?: number): number {
    if (typeof type === 'number') {
      return this.getTpl(type).length;
    } else {
      let num = 0;
      // tslint:disable-next-line: forin
      for (let key in this.source) {
        num += this.source[key].templates.length;
      }
      return num;
    }
  }

  /**
   * 通过复用索引获取模板
   * @param type 消息类型
   * @param actItem 当前模板
   */
  getTemplateByRefMap(type: number, actItem?: any): any[] {
    const iterable = this.provinceRefMap[type] || [];
    let target = [];
    for (const item of iterable) {
      const _template = this.getTpl(item.type) || [];
      target = target.concat(_template.filter(v => v !== actItem));
    }
    return target;
  }

  /** 更新排序 */
  updatePriority(type: number): void {
    const reuslt = this.getTpl(type) || [];
    reuslt.forEach((v, i) => {
      this.validateName(v.name, i, type);
      v.priority = i + 1;
    });
  }

  /** 该消息类型下是否有默认模板 */
  hasDefaultTpl(type: number) {
    const result = this.getTpl(type);
    if (result) return result.some(it => it.isDefault);
  }
}
